import { useState, FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2, MessageCircle } from "lucide-react";
import { createLead } from "@/lib/api";
import { waLink } from "@/lib/content";

type Fields = { name: string; phone: string; email: string; goal: string };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-charcoal-soft">{label}</span>
      {children}
    </label>
  );
}

export function LeadForm({ idPrefix = "book", title = "Let's Talk About Your Goals" }: { idPrefix?: string; title?: string }) {
  const { pathname } = useLocation();
  const [form, setForm] = useState<Fields>({ name: "", phone: "", email: "", goal: "" });
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const set = (k: keyof Fields) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setState("sending");
    try {
      await createLead({ ...form, source_page: pathname });
      setState("done");
      toast.success("Thank you! Sony will reach out to you shortly.");
    } catch (err: any) {
      setState("idle");
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg?.replace("Value error, ", "") ?? "Something went wrong." : "Something went wrong. Please try WhatsApp instead.");
    }
  };

  if (state === "done") {
    return (
      <motion.div data-testid={`${idPrefix}-success`} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[28px] bg-beige-soft p-8 text-charcoal sm:p-10">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-forest text-cream"><Check className="h-6 w-6" /></span>
        <h3 className="mt-6 font-display text-4xl text-forest">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-charcoal-soft">Sony will contact you on {form.phone} to set up your free 20-minute consultation.</p>
        <a href={waLink(`Hi Sony, I'm ${form.name}. I just booked a free consultation.`)} target="_blank" rel="noreferrer" className="btn-ghost mt-6" data-testid={`${idPrefix}-success-whatsapp`}>
          <MessageCircle className="h-4 w-4" /> Say hello on WhatsApp
        </a>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid={`${idPrefix}-form`} className="rounded-[28px] bg-beige-soft p-6 text-charcoal shadow-[0_40px_80px_-40px_rgba(0,0,0,0.6)] sm:p-9">
      <h3 className="font-display text-3xl text-forest sm:text-4xl">{title}</h3>
      <p className="mt-2 text-sm text-charcoal-soft">Fill in your details and Sony will get in touch with you.</p>
      <div className="mt-7 grid gap-4">
        <Field label="Your name"><input className="field" value={form.name} onChange={set("name")} placeholder="Full name" autoComplete="name" data-testid={`${idPrefix}-name-input`} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Phone number"><input className="field" value={form.phone} onChange={set("phone")} placeholder="10-digit number" inputMode="tel" autoComplete="tel" data-testid={`${idPrefix}-phone-input`} /></Field>
          <Field label="Email address"><input className="field" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" data-testid={`${idPrefix}-email-input`} /></Field>
        </div>
        <Field label="Your goals (optional)"><textarea className="field min-h-[110px] resize-none" value={form.goal} onChange={set("goal")} placeholder="What would you like to change?" data-testid={`${idPrefix}-goal-input`} /></Field>
      </div>
      <button type="submit" disabled={state === "sending"} data-testid={`${idPrefix}-submit-button`} className="btn-gold group mt-6 w-full !py-4 text-base">
        {state === "sending" ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Book Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" /></>}
      </button>
      <p className="mt-4 text-center text-xs text-charcoal-soft">Free 20-minute consultation. No obligation.</p>
    </form>
  );
}
